// CreateHospitalType.tsx
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createHospitalType,
  getAllHospitalTypes,
  getHospitalTypeByID,
  updateHospitalType,
} from "./services/hospitalType.service";
import { clearHospitalTypeDetails } from "./hospitalTypeSlice";

import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";

const CreateHospitalType: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const HospitalTypeID = useAppSelector((state) => state.drawer.data);
  const HospitalTypeDetail = useAppSelector(
    (state) => state.hospitalType.HospitalTypeDetail,
  );
  const loading = useAppSelector((state) => state.hospitalType.formLoading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [HospitalTypeDetail]);

  useEffect(() => {
    if (HospitalTypeID) {
      dispatch(getHospitalTypeByID(HospitalTypeID));
    }
    return () => {
      dispatch(clearHospitalTypeDetails());
    };
  }, [HospitalTypeID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ?? "",
      IsActive: values.IsActive === 1,
    };

    try {
      if (HospitalTypeID) {
        const editPayload = {
          ...formData,
          ID: HospitalTypeID,
        };

        const response = await dispatch(updateHospitalType(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllHospitalTypes());
          dispatch(clearHospitalTypeDetails());
        }
      } else {
        const response = await dispatch(createHospitalType(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllHospitalTypes());
          dispatch(clearHospitalTypeDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={HospitalTypeDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {HospitalTypeID
                      ? "Update Hospital Type"
                      : "Add Hospital Type"}
                  </Typography.h4>

                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Title"
                    label="Hospital Type Title"
                    placeholder="Enter hospital type title"
                    maxLength="200"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={HospitalTypeID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateHospitalType;
